import { CopyIcon, DeleteIcon, PlusSquareIcon } from "@chakra-ui/icons";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Flex,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputRightAddon,
  ListItem,
  Select,
  Text,
  UnorderedList,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { Post } from "api";
import { useRef, useState } from "react";
import { extractAdjustTrackerId, extractPackageName } from "utils";

function removeEmptySpaces(str: string) {
  return str.trim().replace(/\s/g, "");
}

export default function AdjustTab() {
  const toast = useToast();
  const {
    isOpen: tokenDialogIsOpen,
    onOpen: tokenDialogOnOpen,
    onClose: tokenDialogOnClose,
  } = useDisclosure({
    id: "event-token-paste-dialog",
  });

  const {
    isOpen: trackerUrlDialogIsOpen,
    onOpen: trackerUrlDialogOnOpen,
    onClose: trackerUrlDialogOnClose,
  } = useDisclosure({
    id: "tracker-url-paste-dialog",
  });

  const cancelTokenRef = useRef();
  const cancelTrackerUrlRef = useRef();
  const eventTokenInputRef = useRef<HTMLInputElement>(null);
  const trackerUrlInputRef = useRef<HTMLInputElement>(null);
  const platformSelectionRef = useRef<HTMLSelectElement>(null);
  const storeUrlInputRef = useRef<HTMLInputElement>(null);

  const [eventTokens, setEventTokens] = useState<string[]>([]);
  const [temporaryTokens, setTemporaryTokens] = useState<string[]>([]);
  const [temporaryTrackerId, setTemporaryTrackerId] = useState<string>("");
  const [generatedLink, setGeneratedLink] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const handleAddEventToken = (token: string) => {
    const trimmedToken = removeEmptySpaces(token);
    setEventTokens((prevTokens) => [...prevTokens, trimmedToken]);
  };

  const handleRemoveEventToken = (index: number) => {
    setEventTokens((prevTokens) => {
      const newTokens = [...prevTokens];
      newTokens.splice(index, 1);
      return newTokens;
    });
  };

  const handleEditEventToken = (index: number, token: string) => {
    const trimmedToken = removeEmptySpaces(token);
    setEventTokens((prevTokens) => {
      const newTokens = [...prevTokens];
      newTokens[index] = trimmedToken;
      return newTokens;
    });
  };

  const handlePasteTrackerUrl = (
    event: React.ClipboardEvent<HTMLInputElement>
  ) => {
    const pastedData = event.clipboardData.getData("Text");
    const isUrl = pastedData.startsWith("http");

    if (!isUrl) {
      setTemporaryTrackerId(pastedData);
      trackerUrlDialogOnOpen();
      return;
    }

    const extractedId = extractAdjustTrackerId(pastedData);

    setTemporaryTrackerId(extractedId ?? "");
    trackerUrlDialogOnOpen();
  };

  const handleTrackerIdAdd = () => {
    trackerUrlInputRef.current!.value = temporaryTrackerId;
    trackerUrlDialogOnClose();
    toast({
      title: "Tracker ID added.",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  const handlePasteToken = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedData = event.clipboardData.getData("Text");
    const splitTokens = pastedData.split("\n");

    setTemporaryTokens(splitTokens.map((token) => removeEmptySpaces(token)));
    tokenDialogOnOpen();
  };

  const handleTemporaryTokensAdd = () => {
    setEventTokens((prevTokens) => [...prevTokens, ...temporaryTokens]);
    tokenDialogOnClose();
    toast({
      title: "Event tokens added.",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  const onGenerateClick = async () => {
    //url: /manage/link-builder/generate-click-url

    const trackerUrl = trackerUrlInputRef.current?.value ?? "";
    const platform = platformSelectionRef.current?.value ?? "";
    const storeUrl = storeUrlInputRef.current?.value ?? "";

    if (trackerUrl === "") {
      toast({
        title: "Tracker URL cannot be empty.",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    if (platform === "") {
      toast({
        title: "Platform cannot be empty.",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    if (storeUrl === "") {
      toast({
        title: "Store URL cannot be empty.",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    if (eventTokens.length === 0) {
      toast({
        title: "No event tokens added.",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    try {
      setLoading(true);
      const body = {
        packageName:
          extractPackageName(storeUrlInputRef.current?.value ?? "") ?? "",
        trackerId: trackerUrlInputRef.current?.value ?? "",
        platformId: platform === "android" ? 1 : 2,
        eventTokens,
        mmp: "adjust",
      };

      const generateClickUrlResponse = await Post(
        "/manage/link-builder/generate-click-url",
        body
      );

      if (generateClickUrlResponse.error) {
        toast({
          title: "Error",
          description: generateClickUrlResponse.error,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      } else {
        setGeneratedLink(generateClickUrlResponse.clickUrl);
        toast({
          title: "Success",
          description: "Click URL generated successfully.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    //reset inputs
    trackerUrlInputRef.current!.value = "";
    storeUrlInputRef.current!.value = "";
    setEventTokens([]);
    platformSelectionRef.current!.value = "";
  };

  return (
    <>
      <Flex flexDir={"column"} pt={8}>
        <Flex gap={4}>
          <Flex
            flex={1}
            flexDir={"column"}
            borderRightWidth={2}
            borderRightColor={"gray.200"}
            p={4}
          >
            <Heading mb={4} size={"md"}>
              Setup Details
            </Heading>
            <FormLabel>Tracker URL</FormLabel>
            <Input
              mb={4}
              onPaste={handlePasteTrackerUrl}
              ref={trackerUrlInputRef}
              placeholder="Tracker URL"
            />
            <FormLabel>Platform</FormLabel>
            <Select
              mb={4}
              placeholder="Select Platform"
              ref={platformSelectionRef}
            >
              <option value="ios">iOS</option>
              <option value="android">Android</option>
            </Select>
            <FormLabel>Store URL</FormLabel>
            <Input mb={4} placeholder={"Store URL"} ref={storeUrlInputRef} />
          </Flex>

          <Flex
            key={`partner detail input - secondary emails`}
            flexDir={"column"}
            gridColumn={"span 2"}
            flex={2}
            p={4}
            gap={4}
          >
            <Heading size={"md"}>Event Tokens</Heading>
            <InputGroup>
              <Input
                placeholder={"Event Token"}
                enterKeyHint={"send"}
                onPaste={handlePasteToken}
                ref={eventTokenInputRef}
                onKeyDown={(e) => {
                  if (e.code === "Space") e.preventDefault();

                  if (e.key === "Enter") {
                    handleAddEventToken(
                      eventTokenInputRef.current?.value ?? ""
                    );
                    toast({
                      title: "Event token added.",
                      status: "success",
                      duration: 2000,
                      isClosable: true,
                    });
                    eventTokenInputRef.current!.value = "";
                  }
                }}
              />

              <InputRightAddon
                _hover={{
                  cursor: "pointer",
                  bgColor: "brand.500",
                  color: "white",
                }}
                transition={"all 0.1s ease-in-out"}
                color={"brand.500"}
                onClick={() => {
                  handleAddEventToken(eventTokenInputRef.current?.value ?? "");
                  toast({
                    title: "Event token added.",
                    status: "success",
                    duration: 2000,
                    isClosable: true,
                  });
                  eventTokenInputRef.current!.value = "";
                }}
                children={
                  <PlusSquareIcon
                    bgColor="transparent"
                    aria-label="Add New Event Token"
                  />
                }
              />
            </InputGroup>
            <Flex flexDir={"column"} gap={2}>
              {eventTokens.map((token, index) => (
                <InputGroup>
                  <Input
                    value={token}
                    onKeyDown={(e) => {
                      if (e.code === "Space") e.preventDefault();
                    }}
                    onChange={(e) =>
                      handleEditEventToken(index, e.target.value)
                    }
                  />
                  <InputRightAddon
                    _hover={{
                      cursor: "pointer",
                      bgColor: "red.500",
                      color: "white",
                    }}
                    transition={"all 0.1s ease-in-out"}
                    color={"red.500"}
                    children={<DeleteIcon />}
                    onClick={() => {
                      handleRemoveEventToken(index);
                      toast({
                        title: "Even token removed.",
                        status: "success",
                        duration: 2000,
                        isClosable: true,
                      });
                    }}
                  />
                </InputGroup>
              ))}
              {eventTokens.length === 0 && (
                <Flex justifyContent={"center"}>
                  <Text color="gray.500" size={"sm"}>
                    No event tokens added.
                  </Text>
                </Flex>
              )}
            </Flex>
          </Flex>
        </Flex>
        <Flex justifyContent={"flex-end"}>
          <Button
            alignSelf={"flex-end"}
            borderColor="brand.500"
            borderWidth={1}
            backgroundColor={"white"}
            _hover={{ backgroundColor: "brand.400", color: "white" }}
            color={"brand.500"}
            transition={"all 0.1s ease-in-out"}
            onClick={reset}
            mr={4}
          >
            Reset
          </Button>
          <Button
            backgroundColor="brand.500"
            _hover={{ backgroundColor: "brand.600" }}
            color={"white"}
            transition={"all 0.1s ease-in-out"}
            onClick={onGenerateClick}
            isLoading={loading}
          >
            Generate
          </Button>
        </Flex>

        <Flex
          display={generatedLink !== "" ? "flex" : "none"}
          flexDir={"column"}
          transition={"all 0.1s ease-in-out"}
          mt={4}
        >
          <Heading size={"md"}>Generated Link</Heading>
          <InputGroup mt={4}>
            <Input value={generatedLink} isReadOnly />
            <InputRightAddon
              _hover={{
                cursor: "pointer",
                bgColor: "brand.500",
                color: "white",
              }}
              transition={"all 0.1s ease-in-out"}
              color={"brand.500"}
              children={<CopyIcon />}
              onClick={() => {
                navigator.clipboard.writeText(generatedLink);
                toast({
                  title: "Copied to clipboard.",
                  status: "success",
                  duration: 2000,
                  isClosable: true,
                });
              }}
            />
          </InputGroup>
        </Flex>
      </Flex>
      <AlertDialog
        isOpen={tokenDialogIsOpen}
        leastDestructiveRef={cancelTokenRef}
        onClose={tokenDialogOnClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleTemporaryTokensAdd();
              }
            }}
          >
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Paste Event Tokens
            </AlertDialogHeader>

            <AlertDialogBody>
              {temporaryTokens.length > 1
                ? "These tokens will be added"
                : "This token will be added"}
              : <br />
              <UnorderedList>
                {temporaryTokens.map((token) => (
                  <ListItem>{token}</ListItem>
                ))}
              </UnorderedList>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelTokenRef} onClick={tokenDialogOnClose}>
                Cancel
              </Button>
              <Button
                colorScheme="green"
                onClick={handleTemporaryTokensAdd}
                ml={3}
              >
                Add
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <AlertDialog
        isOpen={trackerUrlDialogIsOpen}
        leastDestructiveRef={cancelTrackerUrlRef}
        onClose={trackerUrlDialogOnClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleTrackerIdAdd();
              }
            }}
          >
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Detected Tracker ID
            </AlertDialogHeader>

            <AlertDialogBody>
              The following Tracker ID will be added: <br />
              <UnorderedList>
                <ListItem>{temporaryTrackerId}</ListItem>
              </UnorderedList>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelTrackerUrlRef}
                onClick={trackerUrlDialogOnClose}
              >
                Cancel
              </Button>
              <Button colorScheme="green" onClick={handleTrackerIdAdd} ml={3}>
                Add
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
